import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, UsersThree, UserGear, GlobeHemisphereWest } from "@phosphor-icons/react";

const OPTIONS = [
  { value: "private", label: "private", desc: "Only you can see this contact.", icon: Lock, color: "var(--text-3)" },
  { value: "shared", label: "shared with team", desc: "Visible to every member of the organization.", icon: UsersThree, color: "var(--accent)" },
  { value: "team_managed", label: "team-managed", desc: "Visible AND editable by org admins/managers.", icon: UserGear, color: "#FF9500" },
];

export default function VisibilityPicker({ contactId }) {
  const [state, setState] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    const run = async () => {
      try {
        const [v, o] = await Promise.all([
          api.get(`/contacts/${contactId}/visibility`),
          api.get("/organizations"),
        ]);
        setState(v.data);
        setOrgs(o.data.organizations || []);
      } catch { setState(null); }
    };
    run();
  }, [contactId]);

  // If the user is in no org, show a dim "unavailable" message, not nothing — makes feature discoverable.
  if (!state) return null;

  const currentOrgId = state.shared_with_org_id || orgs[0]?.id || null;

  const save = async (visibility, orgId) => {
    setSaving(true);
    try {
      const payload = { visibility };
      if (visibility !== "private") payload.organization_id = orgId || currentOrgId;
      const r = await api.put(`/contacts/${contactId}/visibility`, payload);
      setState({ visibility: r.data.visibility, shared_with_org_id: r.data.shared_with_org_id });
      toast.success(`Visibility: ${r.data.visibility.replace("_", " ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const noOrg = orgs.length === 0;

  return (
    <div className="border p-4" style={{ borderColor: "var(--border)" }} data-testid="visibility-picker">
      <div className="overline mb-3 flex items-center gap-2">
        <GlobeHemisphereWest weight="bold" /> // visibility
      </div>

      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = state.visibility === opt.value;
          const disabled = (opt.value !== "private" && noOrg) || saving;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => save(opt.value, currentOrgId)}
              disabled={disabled}
              data-testid={`vis-${opt.value}`}
              className="w-full text-left p-3 border flex items-start gap-3 disabled:opacity-40 hover:bg-[#15303A]"
              style={{
                borderColor: active ? opt.color : "var(--border)",
                background: active ? "rgba(0,210,255,0.05)" : "transparent",
              }}            >
              <Icon weight={active ? "fill" : "bold"} style={{ color: opt.color, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
              <div className="min-w-0">
                <div className="font-semibold text-sm" style={{ color: active ? opt.color : "var(--text-1)" }}>{opt.label}</div>
                <div className="overline mt-1">{opt.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {!noOrg && state.visibility !== "private" && orgs.length > 1 && (
        <div className="mt-3">
          <div className="overline mb-1">organization</div>
          <select
            value={state.shared_with_org_id || orgs[0].id}
            onChange={(e) => save(state.visibility, e.target.value)}
            className="w-full px-2 py-1 border bg-transparent text-sm"
            style={{ borderColor: "var(--border)" }}
            data-testid="vis-org-select"
          >
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {noOrg && (
        <div className="text-xs mt-3" style={{ color: "var(--text-3)" }} data-testid="vis-no-org">
          Create or join an organization in <code>/organizations</code> to share contacts with teammates.
        </div>
      )}
    </div>
  );
}
